import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../context/DataContext';

const { FiSave, FiArrowLeft, FiUpload, FiX } = FiIcons;

const AddVehicle = () => {
  const navigate = useNavigate();
  const { addVehicle } = useData();
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const brands = [
    'Audi', 'BMW', 'Mercedes-Benz', 'Volkswagen', 'Ford', 
    'Peugeot', 'Citroën', 'Renault', 'Seat', 'Opel', 
    'Toyota', 'Nissan', 'Hyundai', 'Kia'
  ];
  
  const fuelTypes = ['Gasolina', 'Diésel', 'Híbrido', 'Eléctrico', 'GLP'];
  const transmissionTypes = ['Manual', 'Automático'];
  const vehicleTypes = ['Utilitario', 'Berlina', 'SUV', 'Familiar', 'Coupé', 'Cabrio', 'Monovolumen'];
  const colors = ['Blanco', 'Negro', 'Gris', 'Plata', 'Azul', 'Rojo', 'Verde', 'Amarillo', 'Naranja', 'Marrón'];
  
  const commonFeatures = [
    'Aire acondicionado', 'GPS', 'Bluetooth', 'Sensores aparcamiento', 
    'Cámara trasera', 'Llantas aleación', 'Xenón/LED', 'Asientos calefactados',
    'Techo panorámico', 'Cuero', 'Climatizador', 'Control crucero',
    'Sensores lluvia', 'Espejos eléctricos', 'Cristales tintados'
  ];

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImages(prev => [...prev, e.target.result]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    if (images.length === 0) {
      toast.error('Debes subir al menos una imagen');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Process features
      const selectedFeatures = commonFeatures.filter(
        feature => data[`feature_${feature}`]
      );

      // Clean up form data
      const vehicleData = {
        brand: data.brand,
        model: data.model,
        version: data.version,
        year: parseInt(data.year),
        price: parseInt(data.price),
        kilometers: parseInt(data.kilometers),
        fuel: data.fuel,
        transmission: data.transmission,
        doors: parseInt(data.doors),
        color: data.color,
        power: parseInt(data.power),
        owners: parseInt(data.owners),
        itv: data.itv,
        type: data.type,
        description: data.description,
        features: selectedFeatures,
        images: images
      };

      // Add vehicle
      await addVehicle(vehicleData);
      toast.success('Vehículo añadido correctamente');
      navigate('/admin/vehiculos');
    } catch (error) {
      toast.error('Error al añadir el vehículo');
      console.error('Error adding vehicle:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center space-x-4 mb-8">
        <button 
          onClick={() => navigate('/admin/vehiculos')}
          className="p-2 text-gray-600 hover:text-[#005c91] transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-[#2c3e50]">Añadir Nuevo Vehículo</h1>
          <p className="text-gray-600 mt-2">Completa la información del vehículo</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-bold text-[#2c3e50] mb-6">Información Básica</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Marca *
              </label>
              <select
                {...register('brand', { required: 'La marca es obligatoria' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              >
                <option value="">Selecciona una marca</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
              {errors.brand && (
                <p className="text-red-500 text-sm mt-1">{errors.brand.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Modelo *
              </label>
              <input
                {...register('model', { required: 'El modelo es obligatorio' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                placeholder="Ej: Serie 3, A4, Clase C"
              />
              {errors.model && (
                <p className="text-red-500 text-sm mt-1">{errors.model.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Versión
              </label>
              <input
                {...register('version')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                placeholder="Ej: 320d, 2.0 TDI"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Año *
              </label>
              <input
                type="number"
                {...register('year', {
                  required: 'El año es obligatorio',
                  min: { value: 1990, message: 'El año debe ser mayor a 1990' },
                  max: { value: new Date().getFullYear(), message: 'El año no puede ser futuro' }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                placeholder="2020"
              />
              {errors.year && (
                <p className="text-red-500 text-sm mt-1">{errors.year.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Precio (€) *
              </label>
              <input
                type="number"
                {...register('price', {
                  required: 'El precio es obligatorio',
                  min: { value: 1000, message: 'El precio debe ser mayor a 1000€' }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                placeholder="25000"
              />
              {errors.price && (
                <p className="text-red-500 text-sm mt-1">{errors.price.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kilómetros *
              </label>
              <input
                type="number"
                {...register('kilometers', {
                  required: 'Los kilómetros son obligatorios',
                  min: { value: 0, message: 'Los kilómetros no pueden ser negativos' }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                placeholder="50000"
              />
              {errors.kilometers && (
                <p className="text-red-500 text-sm mt-1">{errors.kilometers.message}</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Technical Specifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-bold text-[#2c3e50] mb-6">Especificaciones Técnicas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Combustible *
              </label>
              <select
                {...register('fuel', { required: 'El combustible es obligatorio' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              >
                <option value="">Selecciona combustible</option>
                {fuelTypes.map(fuel => (
                  <option key={fuel} value={fuel}>{fuel}</option>
                ))}
              </select>
              {errors.fuel && (
                <p className="text-red-500 text-sm mt-1">{errors.fuel.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transmisión *
              </label>
              <select
                {...register('transmission', { required: 'La transmisión es obligatoria' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              >
                <option value="">Selecciona transmisión</option>
                {transmissionTypes.map(transmission => (
                  <option key={transmission} value={transmission}>{transmission}</option>
                ))}
              </select>
              {errors.transmission && (
                <p className="text-red-500 text-sm mt-1">{errors.transmission.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Vehículo *
              </label>
              <select
                {...register('type', { required: 'El tipo es obligatorio' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              >
                <option value="">Selecciona tipo</option>
                {vehicleTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.type && (
                <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Puertas *
              </label>
              <select
                {...register('doors', { required: 'Las puertas son obligatorias' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              >
                <option value="">Número de puertas</option>
                <option value="3">3 puertas</option>
                <option value="4">4 puertas</option>
                <option value="5">5 puertas</option>
              </select>
              {errors.doors && (
                <p className="text-red-500 text-sm mt-1">{errors.doors.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color *
              </label>
              <select
                {...register('color', { required: 'El color es obligatorio' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              >
                <option value="">Selecciona color</option>
                {colors.map(color => (
                  <option key={color} value={color}>{color}</option>
                ))}
              </select>
              {errors.color && (
                <p className="text-red-500 text-sm mt-1">{errors.color.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Potencia (CV) *
              </label>
              <input
                type="number"
                {...register('power', {
                  required: 'La potencia es obligatoria',
                  min: { value: 50, message: 'La potencia debe ser mayor a 50 CV' }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                placeholder="150"
              />
              {errors.power && (
                <p className="text-red-500 text-sm mt-1">{errors.power.message}</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Additional Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-bold text-[#2c3e50] mb-6">Información Adicional</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Propietarios *
              </label>
              <select
                {...register('owners', { required: 'El número de propietarios es obligatorio' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              >
                <option value="">Número de propietarios</option>
                <option value="1">1 propietario</option>
                <option value="2">2 propietarios</option>
                <option value="3">3 propietarios</option>
                <option value="4">4 o más propietarios</option>
              </select>
              {errors.owners && (
                <p className="text-red-500 text-sm mt-1">{errors.owners.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ITV hasta *
              </label>
              <input
                type="date"
                {...register('itv', { required: 'La fecha de ITV es obligatoria' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              />
              {errors.itv && (
                <p className="text-red-500 text-sm mt-1">{errors.itv.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción
            </label>
            <textarea
              {...register('description')}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
              placeholder="Describe las características destacadas del vehículo..."
            />
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-bold text-[#2c3e50] mb-6">Equipamiento</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {commonFeatures.map((feature) => (
              <label 
                key={feature} 
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <input
                  type="checkbox"
                  {...register(`feature_${feature}`)}
                  className="w-4 h-4 text-[#005c91] border-gray-300 rounded focus:ring-[#005c91]"
                />
                <span className="text-sm font-medium text-gray-700">{feature}</span>
              </label>
            ))}
          </div>
        </motion.div>

        {/* Images */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-bold text-[#2c3e50] mb-6">Imágenes del Vehículo</h2>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <label htmlFor="image-upload" className="cursor-pointer">
              <SafeIcon icon={FiUpload} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-600 mb-2">
                Haz clic para subir imágenes
              </p>
              <p className="text-sm text-gray-500">
                Puedes seleccionar múltiples imágenes (JPG, PNG, GIF)
              </p>
            </label>
          </div>
          
          {images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <div key={index} className="relative">
                  <img 
                    src={image} 
                    alt={`Imagen ${index + 1}`} 
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Submit Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex justify-end space-x-4"
        >
          <button
            type="button"
            onClick={() => navigate('/admin/vehiculos')}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold transition-colors"
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-[#005c91] hover:bg-[#3498db] disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors flex items-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiSave} className="w-5 h-5" />
                <span>Guardar Vehículo</span>
              </>
            )}
          </button>
        </motion.div>
      </form>
    </div>
  );
};

export default AddVehicle;